import { useState } from 'react'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { TextMessage } from './messages/TextMessage.jsx'
import { ButtonsMessage } from './messages/ButtonsMessage.jsx'
import { ListMessage } from './messages/ListMessage.jsx'
import { ActionMessage } from './messages/ActionMessage.jsx'
import { TypingIndicator } from './messages/TypingIndicator.jsx'
import { getMessageTime } from '../../lib/formatters.js'
import {
  combineInlineStripForMessage,
  resolveMessageStripDynamics,
  shouldShowSuggestionsStrip,
  shouldUseInlineSuggestions,
} from '../../lib/chatUtils.js'
import { useIntelligentWeb } from '../../context/IntelligentWebContext.jsx'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport.js'
import { cn } from '../../lib/cn.js'
import { SuggestionsStrip } from './SuggestionsStrip.jsx'
import { SuggestedPrompts } from './SuggestedPrompts.jsx'
import { MessageQuickReplies } from './messages/MessageQuickReplies.jsx'

const RENDERERS = {
  text: TextMessage,
  /** Plural matches the streaming transport; singular kept for older persisted rows. */
  buttons: ButtonsMessage,
  button: ButtonsMessage,
  list: ListMessage,
  actions: ActionMessage,
  action: ActionMessage,
  typing_indicator: TypingIndicator,
}

/**
 * One row in the message list. Owns:
 *   - bubble alignment (visitor vs chatbot),
 *   - per-message timestamp,
 *   - optional thumbs-up/thumbs-down vote buttons (last bot message
 *     only, parity with v1).
 *
 * Styling for the bubble background uses inline styles backed by the
 * customer's theme colors because Tailwind can't generate arbitrary
 * runtime colors at build time. Border + radius come from theme
 * variables on `:host` so template overrides in `templates/` cascade
 * naturally.
 */
export function MessageBubble({
  message,
  conf,
  lastItem,
  /** Latest completed bot row in the thread (not necessarily `lastItem` when visitor sent next). */
  isLatestBotTurn = false,
  ochatbot,
  layoutKey = 'overlay',
  onSend,
}) {
  const [voted, setVoted] = useState(false)
  const { activeCtas, activeLeadForms } = useIntelligentWeb()
  const Renderer = RENDERERS[message.type] || TextMessage
  const isVisitor = message.from === 'visitor'
  const isMobileViewport = useIsMobileViewport()
  const useInlineLayout =
    shouldUseInlineSuggestions(conf, layoutKey) || isMobileViewport
  const inlineStripItems =
    !isVisitor && useInlineLayout
      ? combineInlineStripForMessage(message, {
          activeCtas,
          activeLeadForms,
          conf,
          includeAdminItems: isLatestBotTurn,
        })
      : []
  const showInlineSuggestions =
    shouldShowSuggestionsStrip(conf) &&
    inlineStripItems.length > 0 &&
    message.streaming !== true
  const isSlideOut = layoutKey === 'slide-out'
  const showSuggestedPrompts =
    !isVisitor &&
    Array.isArray(message.suggestedPrompts) &&
    message.suggestedPrompts.length > 0
  const bubbleButtons = Array.isArray(message.buttons) ? message.buttons : []
  const bubbleQuickReplies =
    !isVisitor &&
    bubbleButtons.length > 0 &&
    message.type !== 'buttons' &&
    message.type !== 'button'

  if (message.visible === false) return null

  const handleVote = (score) => {
    const logId = message.additionalParameters?.logId ?? '-1'
    ochatbot?.callAPI(
      `ometrics-response-vote~${logId}_${score}`,
      '',
      false,
    )
    setVoted(true)
  }

  const bubbleStyle = isVisitor
    ? {
        backgroundColor:
          conf?.widgetUserMessageBackgroundColor || 'var(--color-brand)',
        color:
          conf?.widgetUserMessageTextColor ||
          'var(--color-brand-foreground)',
      }
    : {
        backgroundColor:
          conf?.widgetBotMessageBackgroundColor || 'var(--color-muted)',
        color:
          conf?.widgetBotMessageTextColor ||
          'var(--color-surface-foreground)',
      }

  return (
    <li
      data-message-id={message.id}
      data-message-from={message.from}
      className={cn(
        'flex w-full flex-col gap-1',
        isVisitor
          ? 'items-end'
          : message.type === 'list' || showInlineSuggestions
            ? 'items-stretch'
            : 'items-start',
      )}
    >
      <div
        style={bubbleStyle}
        className={cn(
          'rounded-2xl px-3 py-2 shadow-sm',
          isVisitor
            ? 'max-w-[85%] rounded-br-sm'
            : message.type === 'list'
              ? 'w-full min-w-0 max-w-full rounded-bl-sm'
              : 'max-w-[85%] rounded-bl-sm',
        )}
      >
        <Renderer
          message={message}
          conf={conf}
          lastItem={lastItem}
          ochatbot={ochatbot}
        />

        {bubbleQuickReplies ? (
          <MessageQuickReplies
            buttons={bubbleButtons}
            messageId={message.id}
            conf={conf}
            ochatbot={ochatbot}
            className="mt-2"
          />
        ) : null}

        {conf?.displayMessageTime && message.time && (
          <div className="mt-1 text-right text-[10px] opacity-70">
            {getMessageTime(
              new Date(message.time),
              conf.timeFormat || 'shortTime',
              conf.dateTimeFormat || 'mediumDate',
            )}
          </div>
        )}

        {!isVisitor &&
          lastItem &&
          message.type !== 'typing_indicator' &&
          (voted ? (
            <p className="mt-2 text-right text-xs opacity-70">
              Thanks for your response.
            </p>
          ) : (
            <div className="mt-2 flex items-center justify-end gap-1 text-xs opacity-80">
              <span className="mr-1">Was I helpful?</span>
              <button
                type="button"
                aria-label="Vote helpful"
                onClick={() => handleVote(1)}
                className="rounded-full p-1 transition-colors hover:bg-black/10"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label="Vote not helpful"
                onClick={() => handleVote(-1)}
                className="rounded-full p-1 transition-colors hover:bg-black/10"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
      </div>
      {showInlineSuggestions ? (
        <div className="w-full min-w-0 max-w-full pl-0.5">
          <SuggestionsStrip
            suggestions={resolveMessageStripDynamics(message, {
              conf,
              activeLeadForms,
            })}
            variant="inline"
            layoutKey={layoutKey}
            includeAdminItems={isLatestBotTurn}
            onSend={onSend}
            ochatbot={ochatbot}
          />
        </div>
      ) : null}
      {showSuggestedPrompts ? (
        <SuggestedPrompts
          prompts={message.suggestedPrompts}
          onSend={onSend}
          messageId={message.id}
          variant={isSlideOut && showInlineSuggestions ? 'belowStrip' : 'default'}
        />
      ) : null}
    </li>
  )
}
